import React, { useState, useEffect, useMemo } from 'react';
import GlobalTabs from '../GlobalTabs';
import SmallServiceCard from '../SmallServiceCard';
import { StatusBadge } from '../StatusBadge';
import styles from './TraceabilityTab.module.css';
import { useLocale } from '../../context/DynamicLocaleProvider';
import { useToast } from '../../context/ToastProvider';
import { useTheme } from '../../context/ThemeProvider';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { 
  UserRequirement, 
  FunctionalSpecification, 
  TestCase, 
  RiskRecord, 
  StandardOperatingProcedure 
} from '../../types';

interface TraceabilityTabProps {
  projectId: string;
  onExport?: () => void;
  onEntityClick?: (entityId: string, entityType: 'URS' | 'FS' | 'TEST' | 'RISK' | 'SOP') => void;
}

interface TraceabilityRow {
  urs: UserRequirement | null;
  fs: FunctionalSpecification[];
  tests: TestCase[];
  risks: RiskRecord[];
  sops: StandardOperatingProcedure[];
  missingHints: string[];
  errors: string[];
}

interface EntityMap {
  urs: Map<string, UserRequirement>;
  fs: Map<string, FunctionalSpecification>;
  tests: Map<string, TestCase>;
  risks: Map<string, RiskRecord>;
  sops: Map<string, StandardOperatingProcedure>;
}

export const TraceabilityTab: React.FC<TraceabilityTabProps> = ({ 
  projectId, 
  onExport,
  onEntityClick 
}) => {
  const { t } = useLocale();
  const showToast = useToast();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  const [entities, setEntities] = useState<EntityMap>({
    urs: new Map(),
    fs: new Map(),
    tests: new Map(),
    risks: new Map(),
    sops: new Map()
  });
  const [loading, setLoading] = useState(true);

  // Real-time listeners for all entity types
  useEffect(() => {
    if (!projectId) return;

    const unsubscribers: (() => void)[] = [];

    // URS listener
    const ursUnsub = onSnapshot(
      collection(db, 'projects', projectId, 'urs'),
      (snapshot) => {
        const ursMap = new Map<string, UserRequirement>();
        snapshot.forEach((doc) => {
          const data = doc.data();
          const id = data.id || `URS-${doc.id.substring(0, 8)}`;
          ursMap.set(id, {
            ...data,
            id,
            type: 'URS'
          } as UserRequirement);
        });
        setEntities(prev => ({ ...prev, urs: ursMap }));
        setLoading(false);
      },
      (error) => {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error fetching URS:', error);
        }
        setLoading(false);
      }
    );
    unsubscribers.push(ursUnsub);

    // FS listener
    const fsUnsub = onSnapshot(
      collection(db, 'projects', projectId, 'fs'),
      (snapshot) => {
        const fsMap = new Map<string, FunctionalSpecification>();
        snapshot.forEach((doc) => {
          const data = doc.data();
          const id = data.id || `FS-${doc.id.substring(0, 8)}`;
          fsMap.set(id, {
            ...data,
            id,
            type: 'FS'
          } as FunctionalSpecification);
        });
        setEntities(prev => ({ ...prev, fs: fsMap }));
      }
    );
    unsubscribers.push(fsUnsub);

    // Tests listener
    const testsUnsub = onSnapshot(
      collection(db, 'projects', projectId, 'tests'),
      (snapshot) => {
        const testsMap = new Map<string, TestCase>();
        snapshot.forEach((doc) => {
          const data = doc.data();
          const id = data.id || `TEST-${doc.id.substring(0, 8)}`;
          testsMap.set(id, {
            ...data,
            id,
            type: 'TEST'
          } as TestCase);
        });
        setEntities(prev => ({ ...prev, tests: testsMap }));
      }
    );
    unsubscribers.push(testsUnsub);

    // Risks listener
    const risksUnsub = onSnapshot(
      collection(db, 'projects', projectId, 'risks'),
      (snapshot) => {
        const risksMap = new Map<string, RiskRecord>();
        snapshot.forEach((doc) => {
          const data = doc.data();
          const id = data.id || `RISK-${doc.id.substring(0, 8)}`;
          risksMap.set(id, {
            ...data,
            id,
            type: 'RISK'
          } as RiskRecord);
        });
        setEntities(prev => ({ ...prev, risks: risksMap }));
      }
    );
    unsubscribers.push(risksUnsub);

    // SOPs listener
    const sopsUnsub = onSnapshot(
      collection(db, 'projects', projectId, 'sops'),
      (snapshot) => {
        const sopsMap = new Map<string, StandardOperatingProcedure>();
        snapshot.forEach((doc) => {
          const data = doc.data();
          const id = data.id || `SOP-${doc.id.substring(0, 8)}`;
          sopsMap.set(id, {
            ...data,
            id,
            type: 'SOP'
          } as StandardOperatingProcedure);
        });
        setEntities(prev => ({ ...prev, sops: sopsMap }));
      }
    );
    unsubscribers.push(sopsUnsub);

    return () => {
      unsubscribers.forEach(unsub => unsub());
    };
  }, [projectId]);

  // Build traceability matrix with all relations
  const traceabilityMatrix = useMemo(() => {
    const matrix: TraceabilityRow[] = [];
    const ursList = Array.from(entities.urs.values());
    
    // Build global mapping structures for cross-contamination detection
    // Track which FS belong to which URS
    const fsToUrsMap = new Map<string, Set<string>>();
    entities.fs.forEach(fs => {
      const ursSet = new Set<string>();
      (fs.links?.urs_ids || []).forEach(ursId => {
        // Check if URS exists (by ID in map or in links)
        if (entities.urs.has(ursId)) {
          ursSet.add(ursId);
        }
      });
      // Also check reverse links (URS -> FS)
      ursList.forEach(urs => {
        if ((urs.links?.fs_ids || []).includes(fs.id)) {
          ursSet.add(urs.id);
        }
      });
      if (ursSet.size > 0) {
        fsToUrsMap.set(fs.id, ursSet);
      }
    });
    
    // Track which Risks belong to which FS
    const riskToFsMap = new Map<string, Set<string>>();
    entities.risks.forEach(risk => {
      const fsSet = new Set<string>();
      (risk.links?.fs_ids || []).forEach(fsId => {
        if (entities.fs.has(fsId)) {
          fsSet.add(fsId);
        }
      });
      // Also check reverse links (FS -> Risk)
      entities.fs.forEach(fs => {
        if ((fs.links?.risk_ids || []).includes(risk.id)) {
          fsSet.add(fs.id);
        }
      });
      if (fsSet.size > 0) {
        riskToFsMap.set(risk.id, fsSet);
      }
    });
    
    // Track which Tests belong to which FS
    const testToFsMap = new Map<string, Set<string>>();
    entities.tests.forEach(test => {
      const fsSet = new Set<string>();
      (test.links?.fs_ids || []).forEach(fsId => {
        if (entities.fs.has(fsId)) {
          fsSet.add(fsId);
        }
      });
      // Also check reverse links (FS -> Test)
      entities.fs.forEach(fs => {
        if ((fs.links?.test_ids || []).includes(test.id)) {
          fsSet.add(fs.id);
        }
      });
      if (fsSet.size > 0) {
        testToFsMap.set(test.id, fsSet);
      }
    });
    
    // Track which Tests belong to which Risks
    const testToRiskMap = new Map<string, Set<string>>();
    entities.tests.forEach(test => {
      const riskSet = new Set<string>();
      (test.links?.risk_ids || []).forEach(riskId => {
        if (entities.risks.has(riskId)) {
          riskSet.add(riskId);
        }
      });
      // Also check reverse links (Risk -> Test)
      entities.risks.forEach(risk => {
        if ((risk.links?.test_ids || []).includes(test.id)) {
          riskSet.add(risk.id);
        }
      });
      if (riskSet.size > 0) {
        testToRiskMap.set(test.id, riskSet);
      }
    });

    ursList.forEach(urs => {
      // Get forward links (URS -> FS)
      const forwardFsIds = urs.links?.fs_ids || [];
      const forwardFs = forwardFsIds
        .map(id => entities.fs.get(id))
        .filter((fs): fs is FunctionalSpecification => fs !== undefined);

      // Get reverse links (FS -> URS)
      const reverseFs: FunctionalSpecification[] = [];
      entities.fs.forEach(fs => {
        if (fs.links?.urs_ids?.includes(urs.id)) {
          reverseFs.push(fs);
        }
      });

      // Combine and deduplicate
      const allFs = Array.from(new Map([...forwardFs, ...reverseFs].map(fs => [fs.id, fs])).values());

      // Get all related risks (from FS and URS)
      const allRiskIds = new Set<string>();
      (urs.links?.risk_ids || []).forEach(id => allRiskIds.add(id));
      allFs.forEach(fs => {
        (fs.links?.risk_ids || []).forEach(id => allRiskIds.add(id));
      });
      entities.risks.forEach(risk => {
        if (risk.links?.urs_ids?.includes(urs.id) || risk.links?.fs_ids?.some(fsId => allFs.some(fs => fs.id === fsId))) {
          allRiskIds.add(risk.id);
        }
      });
      const allRisks = Array.from(allRiskIds)
        .map(id => entities.risks.get(id))
        .filter((risk): risk is RiskRecord => risk !== undefined);

      // Get all related tests (from FS, Risks, and URS)
      const allTestIds = new Set<string>();
      allFs.forEach(fs => {
        (fs.links?.test_ids || []).forEach(id => allTestIds.add(id));
      });
      // Also get tests that directly link to URS
      (urs.links?.test_ids || []).forEach(id => allTestIds.add(id));
      // And reverse links from tests
      entities.tests.forEach(test => {
        if (test.links?.urs_ids?.includes(urs.id) || test.links?.fs_ids?.some(fsId => allFs.some(fs => fs.id === fsId))) {
          allTestIds.add(test.id);
        }
      });
      // Get tests from risks
      allRisks.forEach(risk => {
        (risk.links?.test_ids || []).forEach(id => allTestIds.add(id));
      });
      entities.tests.forEach(test => {
        if (test.links?.risk_ids?.some(riskId => allRisks.some(risk => risk.id === riskId))) {
          allTestIds.add(test.id);
        }
      });
      const allTests = Array.from(allTestIds)
        .map(id => entities.tests.get(id))
        .filter((test): test is TestCase => test !== undefined);

      // Get all related SOPs
      const allSopIds = new Set<string>();
      (urs.links?.sop_ids || []).forEach(id => allSopIds.add(id));
      allFs.forEach(fs => {
        (fs.links?.sop_ids || []).forEach(id => allSopIds.add(id));
      });
      entities.sops.forEach(sop => {
        if (sop.links?.urs_ids?.includes(urs.id) || sop.links?.fs_ids?.some(fsId => allFs.some(fs => fs.id === fsId))) {
          allSopIds.add(sop.id);
        }
      });
      const allSops = Array.from(allSopIds)
        .map(id => entities.sops.get(id))
        .filter((sop): sop is StandardOperatingProcedure => sop !== undefined);

      // Check for errors (RED ERRORS)
      const errors: string[] = [];
      
      // ERROR 1: URS must have at least one FS
      if (allFs.length === 0) {
        errors.push('✗ ERROR: URS requires at least one Functional Specification');
      }
      
      // ERROR 2: Check if FS points to multiple URS
      allFs.forEach(fs => {
        const ursSet = fsToUrsMap.get(fs.id);
        if (ursSet && ursSet.size > 1) {
          const otherUrs = Array.from(ursSet).filter(id => id !== urs.id);
          errors.push(`✗ ERROR: FS ${fs.id} belongs to multiple URS: ${urs.id} and ${otherUrs.join(', ')}`);
        }
      });
      
      // ERROR 3: Every FS must have at least one RISK
      allFs.forEach(fs => {
        const fsRiskIds = new Set<string>();
        (fs.links?.risk_ids || []).forEach(id => fsRiskIds.add(id));
        entities.risks.forEach(risk => {
          if (risk.links?.fs_ids?.includes(fs.id)) {
            fsRiskIds.add(risk.id);
          }
        });
        if (fsRiskIds.size === 0) {
          errors.push(`✗ ERROR: FS ${fs.id} requires at least one Risk`);
        }
      });
      
      // ERROR 4: Every RISK must have at least one TEST
      allRisks.forEach(risk => {
        const riskTestIds = new Set<string>();
        (risk.links?.test_ids || []).forEach(id => riskTestIds.add(id));
        entities.tests.forEach(test => {
          if (test.links?.risk_ids?.includes(risk.id)) {
            riskTestIds.add(test.id);
          }
        });
        if (riskTestIds.size === 0) {
          errors.push(`✗ ERROR: Risk ${risk.id} requires at least one Test`);
        }
      });
      
      // ERROR 5: Cascade validation - Check if Risks belong to multiple FS chains from different URS
      allRisks.forEach(risk => {
        const fsSet = riskToFsMap.get(risk.id);
        if (fsSet && fsSet.size > 0) {
          // Check if any of these FS belong to different URS
          const ursSet = new Set<string>();
          fsSet.forEach(fsId => {
            const riskFsUrs = fsToUrsMap.get(fsId);
            if (riskFsUrs) {
              riskFsUrs.forEach(ursId => ursSet.add(ursId));
            }
          });
          // If risk belongs to FS from multiple URS, that's an error
          if (ursSet.size > 1) {
            const allUrs = Array.from(ursSet);
            errors.push(`✗ ERROR: Risk ${risk.id} belongs to FS from multiple URS: ${allUrs.join(', ')}`);
          }
        }
      });
      
      // ERROR 6: Cascade validation - Check if Tests belong to multiple FS chains from different URS
      allTests.forEach(test => {
        const fsSet = testToFsMap.get(test.id);
        if (fsSet && fsSet.size > 0) {
          // Check if any of these FS belong to different URS
          const ursSet = new Set<string>();
          fsSet.forEach(fsId => {
            const testFsUrs = fsToUrsMap.get(fsId);
            if (testFsUrs) {
              testFsUrs.forEach(ursId => ursSet.add(ursId));
            }
          });
          // If test belongs to FS from multiple URS, that's an error
          if (ursSet.size > 1) {
            const allUrs = Array.from(ursSet);
            errors.push(`✗ ERROR: Test ${test.id} belongs to FS from multiple URS: ${allUrs.join(', ')}`);
          }
        }
        
        // Also check if test belongs to risks from different FS chains (which belong to different URS)
        const riskSet = testToRiskMap.get(test.id);
        if (riskSet && riskSet.size > 0) {
          const ursSet = new Set<string>();
          riskSet.forEach(riskId => {
            const riskFsSet = riskToFsMap.get(riskId);
            if (riskFsSet) {
              riskFsSet.forEach(fsId => {
                const riskFsUrs = fsToUrsMap.get(fsId);
                if (riskFsUrs) {
                  riskFsUrs.forEach(ursId => ursSet.add(ursId));
                }
              });
            }
          });
          // If test belongs to risks from FS that belong to multiple URS, that's an error
          if (ursSet.size > 1) {
            const allUrs = Array.from(ursSet);
            errors.push(`✗ ERROR: Test ${test.id} belongs to Risks from FS of multiple URS: ${allUrs.join(', ')}`);
          }
        }
      });

      // Check for missing relations and compliance issues (warnings/info)
      const missingHints: string[] = [];
      
      // Info: Risks are optional but recommended
      if (allRisks.length === 0 && urs.gxp_relevance !== 'None') {
        missingHints.push('ℹ Consider Risks: GxP-relevant URS should have risk assessments');
      }
      
      // Info: SOPs are optional
      if (allSops.length === 0 && urs.gxp_relevance === 'High') {
        missingHints.push('ℹ Consider SOPs: High GxP relevance may require SOPs');
      }

      matrix.push({
        urs,
        fs: allFs,
        tests: allTests,
        risks: allRisks,
        sops: allSops,
        missingHints,
        errors
      });
    });

    return matrix;
  }, [entities]);

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { text: string; variant: 'success' | 'warning' | 'error' | 'info' }> = {
      Approved: { text: '✓ Approved', variant: 'success' },
      Executed: { text: '✓ Executed', variant: 'success' },
      Effective: { text: '✓ Effective', variant: 'success' },
      'In Review': { text: '⚠ In Review', variant: 'warning' },
      Draft: { text: '⏳ Draft', variant: 'info' },
      Retired: { text: '✗ Retired', variant: 'error' },
      Obsolete: { text: '✗ Obsolete', variant: 'error' },
      Deviation: { text: '✗ Deviation', variant: 'error' }
    };
    const statusInfo = statusMap[status] || { text: status, variant: 'info' };
    return <StatusBadge text={statusInfo.text} variant={statusInfo.variant} size="small" />;
  };

  const handleEntityClick = (entityId: string, entityType: 'URS' | 'FS' | 'TEST' | 'RISK' | 'SOP') => {
    if (onEntityClick) {
      onEntityClick(entityId, entityType);
    }
  };

  const tabs = [
    {
      label: t('project_tab_full_traceability') || 'Full Traceability',
      queryValue: 'full-traceability',
      content: (
        <div className={styles.matrixSection}>
          {loading ? (
            <div className={styles.loadingState}>
              <p>{t('global_loading') || 'Loading...'}</p>
            </div>
          ) : traceabilityMatrix.length === 0 ? (
            <div className={styles.emptyState}>
              <p>{t('project_tab_no_traceability_data') || 'No traceability data available. Create URS entities to see the traceability matrix.'}</p>
            </div>
          ) : (
            <div className={styles.matrixTable}>
              <div className={`${styles.matrixHeader} ${styles.fullMatrix}`}>
                <div className={styles.matrixCell}>URS</div>
                <div className={styles.matrixCell}>FS</div>
                <div className={styles.matrixCell}>Risks</div>
                <div className={styles.matrixCell}>Tests</div>
                <div className={styles.matrixCell}>SOPs</div>
                <div className={styles.matrixCell}>Status</div>
              </div>
              {traceabilityMatrix.map((row, index) => {
                const hasErrors = row.errors.length > 0;
                return (
                  <div 
                    key={row.urs?.id || index} 
                    className={`${styles.matrixRow} ${styles.fullMatrix} ${hasErrors ? styles.errorRow : ''}`}
                  >
                    {/* URS */}
                    <div className={`${styles.matrixCell} ${row.errors.some(e => e.includes('URS requires')) ? styles.errorCell : ''}`}>
                      {row.urs ? (
                        <button
                          className={styles.entityLink}
                          onClick={() => handleEntityClick(row.urs!.id, 'URS')}
                          title={row.urs.title}
                        >
                          <span className={styles.entityId}>{row.urs.id}</span>
                        </button>
                      ) : (
                        <span className={styles.emptyCell}>—</span>
                      )}
                    </div>

                    {/* FS */}
                    <div className={`${styles.matrixCell} ${row.errors.some(e => e.includes('FS') && (e.includes('belongs to multiple') || e.includes('requires at least one Risk'))) ? styles.errorCell : ''}`}>
                      {row.fs.length > 0 ? (
                        <div className={styles.entityList}>
                          {row.fs.map((fs) => {
                            const fsHasError = row.errors.some(e => e.includes(`FS ${fs.id}`));
                            return (
                              <button
                                key={fs.id}
                                className={`${styles.entityLink} ${fsHasError ? styles.errorLink : ''}`}
                                onClick={() => handleEntityClick(fs.id, 'FS')}
                                title={fs.title}
                              >
                                <span className={styles.entityId}>{fs.id}</span>
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <span className={styles.emptyCell}>—</span>
                      )}
                    </div>

                    {/* Risks */}
                    <div className={`${styles.matrixCell} ${row.errors.some(e => e.includes('Risk') && e.includes('requires at least one Test')) ? styles.errorCell : ''}`}>
                      {row.risks.length > 0 ? (
                        <div className={styles.entityList}>
                          {row.risks.map((risk) => {
                            const riskHasError = row.errors.some(e => e.includes(`Risk ${risk.id}`));
                            return (
                              <button
                                key={risk.id}
                                className={`${styles.entityLink} ${riskHasError ? styles.errorLink : ''}`}
                                onClick={() => handleEntityClick(risk.id, 'RISK')}
                                title={risk.title}
                              >
                                <span className={styles.entityId}>{risk.id}</span>
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <span className={styles.emptyCell}>—</span>
                      )}
                    </div>

                    {/* Tests */}
                    <div className={styles.matrixCell}>
                      {row.tests.length > 0 ? (
                        <div className={styles.entityList}>
                          {row.tests.map((test) => {
                            const testHasError = row.errors.some(e => e.includes(`Test ${test.id}`));
                            return (
                              <button
                                key={test.id}
                                className={`${styles.entityLink} ${testHasError ? styles.errorLink : ''}`}
                                onClick={() => handleEntityClick(test.id, 'TEST')}
                                title={test.title}
                              >
                                <span className={styles.entityId}>{test.id}</span>
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <span className={styles.emptyCell}>—</span>
                      )}
                    </div>

                    {/* SOPs */}
                    <div className={styles.matrixCell}>
                      {row.sops.length > 0 ? (
                        <div className={styles.entityList}>
                          {row.sops.map((sop) => (
                            <button
                              key={sop.id}
                              className={styles.entityLink}
                              onClick={() => handleEntityClick(sop.id, 'SOP')}
                              title={sop.title}
                            >
                              <span className={styles.entityId}>{sop.code || sop.id}</span>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <span className={styles.emptyCell}>—</span>
                      )}
                    </div>

                    {/* Status */}
                    <div className={styles.matrixCell}>
                      {row.urs && getStatusBadge(row.urs.status)}
                    </div>
                    
                    {/* Error Messages */}
                    {row.errors.length > 0 && (
                      <div className={styles.errorMessages}>
                        {row.errors.map((error, errorIndex) => (
                          <div key={errorIndex} className={styles.errorMessage}>
                            {error}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Missing Hints (warnings/info) */}
                    {row.missingHints.length > 0 && (
                      <div className={styles.missingHints}>
                        {row.missingHints.map((hint, hintIndex) => (
                          <div key={hintIndex} className={styles.missingHint}>
                            {hint}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )
    },
    {
      label: t('project_tab_options') || 'Options',
      queryValue: 'options',
      content: (
        <div className={styles.optionsGrid}>
          {/* Export matrix as PDF */}
          <SmallServiceCard
            id="export-traceability-pdf"
            title={t('project_tab_trace_export_pdf') || 'Als PDF exportieren'}
            description={t('project_tab_trace_export_pdf_desc') || 'Exportieren Sie die gesamte Nachverfolgungs-Matrix als ein PDF Dokument.'}
            fileTypeIcon="/assets/graphics/pdf-icon.svg"
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/arrow.down.circle.white.svg" : "/assets/icons/arrow.down.circle.black.svg"}
            buttonText={t('project_tab_export') || 'Exportieren'}
            buttonOnClick={onExport || (() => showToast(t('project_tab_exporting') || 'Exporting...', 'info'))}
            disabled
          />

          {/* Export matrix as CSV */}
          <SmallServiceCard
            id="export-traceability-csv"
            title={t('project_tab_trace_export_csv') || 'Als CSV exportieren'}
            description={t('project_tab_trace_export_csv_desc') || 'Exportieren Sie die gesamte Nachverfolgungs-Matrix als ein CSV Dokument.'}
            fileTypeIcon={"/assets/graphics/csv_icon.svg"}
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/arrow.down.circle.white.svg" : "/assets/icons/arrow.down.circle.black.svg"}
            buttonText={t('project_tab_export') || 'Exportieren'}
            buttonOnClick={onExport || (() => showToast(t('project_tab_exporting') || 'Exporting...', 'info'))}
            disabled
          />
        </div>
      )
    }
  ];

  return (
    <div className={styles.traceabilityTab}>
      <div className={styles.tabHeader}>
        <h2 className={styles.tabTitle}>
          {t('project_page_tab_traceability') || 'Traceability Matrix'}
        </h2>
        {!loading && traceabilityMatrix.length > 0 && (
          <div className={styles.stats}>
            <span className={styles.statItem}>
              {traceabilityMatrix.length} URS
            </span>
            <span className={styles.statItem}>
              {traceabilityMatrix.reduce((sum, row) => sum + row.fs.length, 0)} FS
            </span>
            <span className={styles.statItem}>
              {traceabilityMatrix.reduce((sum, row) => sum + row.tests.length, 0)} Tests
            </span>
            <span className={styles.statItem}>
              {traceabilityMatrix.filter(row => row.errors.length === 0).length}/{traceabilityMatrix.length} Complete
            </span>
          </div>
        )}
      </div>

      <GlobalTabs tabs={tabs} queryParam="tab" defaultTabValue="full-traceability" />
    </div>
  );
};
